import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import SingleCheckBoxField from 'components/form-components/SingleCheckBoxField';
import NumberFieldWithTag from 'components/form-components/NumberFieldWithTag';
import TextField from 'components/form-components/TextField';
import ColorChangeModal from 'components/common-components/ColorChangeModal';
import FormButton from 'components/form-components/FormButton';
import editIcon from 'assets/images/icons/edit.svg';
import DateFieldWithTag from 'components/form-components/DateFieldWithTag';
import SelectField from 'components/form-components/SelectField';
import ColorField from 'components/form-components/ColorField';
import SelectIconField from 'components/form-components/SelectIconField';
import RatingBox from './common-components/RatingBox';
import NumberField from 'components/form-components/NumberField';
import ToggleBox from 'components/toggler-components/ToggleBox';
import ToggleBoxHeader from 'components/toggler-components/ToggleBoxHeader';
import ToggleBoxBody from 'components/toggler-components/ToggleBoxBody';
import ChoiceListOptionList from './choiceListBasicProperties/ChoiceListOptionList';
import DataSourceBasicProperties from './dataSourceBasicProperties/DataSourceBasicProperties';
import ImageFilePicker from './ImageFilePicker';
import ExternalApiBasicProperties from './externalApi/ExternalApiBasicProperties';
import { validateSqlColumnName } from 'pages/forms/utils';
import WatermarkComponent from './watermarkComponents/watermarkComponent';
import Geofencing from './Geofencing';
import ToggleSwitch from 'components/form-components/ToggleSwitch';
import PointerField from 'components/form-components/PointerField';
import TextArea from 'components/form-components/TextArea';
import { maxElementTitleLength } from './utils';
import OtpMessage from './OtpMessage';
import TicketDetailProperties from './customComponentProprties/TicketDetailProperties';
import CompanyFieldBasicProperties from './CompanyFieldBasicProperties';

function SingleFieldProperties({
  onChangeProperties,
  singleProperty,
  fieldSectionProperties,
  propertiesList,
  otherDetails,
  toggleBox,
  dataSourcesList,
  companyFieldList,
  companyFieldMap,
  companyFieldDisplayValues,
  selectedCompanyFieldIds,
  ticketTypeList,
  selectedField,
  hasTriggers,
  geofenceEnabledObject,
}) {
  const { t } = useTranslation();
  const [answerChoiceToggle, setAnswerChoiceToggle] = useState(false);
  const [openColorModal, setOpenColorModal] = useState(false);
  const [openColorTypeModal, setOpenColorTypeModal] = useState('');

  // handling multiple new field name in mention input field
  const allFormElemetsOptionArray = Object.values(otherDetails.allFormElementsObj).map((el) => ({
    id: el.fieldId,
    display: el.fieldName === 'UnTitled' ? el.fieldId : el.fieldName,
    value: el.fieldId,
    type: 'formField',
  }));

  const ticketTypeOptions = useMemo(() =>
    ticketTypeList.map((type) => ({
      displayValue: type.name,
      value: type._id,
    })),
  );

  return (
    <div
      className={`${
        singleProperty.externalCssApply ? 'flex-fill d-flex align-items-end' : 'w-100'
      } ${
        singleProperty.key === 'transparent' || singleProperty.key === 'withoutFrame' ? 'pb-2' : ''
      } ${singleProperty.classes ? singleProperty.classes : ''}`}
    >
      {singleProperty.type === 'toggle' && singleProperty.key === 'optionList' ? (
        <ToggleBox>
          <ToggleBoxHeader
            show={answerChoiceToggle}
            toggleBoxOpen={() => setAnswerChoiceToggle(!answerChoiceToggle)}
            title={t(singleProperty.text)}
          />
          <ToggleBoxBody show={answerChoiceToggle}>
            <ChoiceListOptionList
              setName={fieldSectionProperties.setName}
              optionList={fieldSectionProperties[singleProperty.key]}
              onChangeBasicProperties={(name, value) => {
                onChangeProperties(name, value, toggleBox.defaultPropsKey + 'Properties');
              }}
            />
          </ToggleBoxBody>
        </ToggleBox>
      ) : singleProperty.type === 'toggle' && singleProperty.key === 'dataSource' ? (
        <DataSourceBasicProperties
          dataSourcesList={dataSourcesList}
          otherDetails={otherDetails}
          properties={fieldSectionProperties}
          handleChange={(name, value) => {
            onChangeProperties(name, value, toggleBox.defaultPropsKey + 'Properties');
          }}
        />
      ) : singleProperty.type === 'toggle' && singleProperty.key === 'externalApi' ? (
        <ExternalApiBasicProperties
          otherDetails={otherDetails}
          properties={fieldSectionProperties}
          handleChange={(name, value) => {
            onChangeProperties(name, value, toggleBox.defaultPropsKey + 'Properties');
          }}
        />
      ) : singleProperty.type === 'toggle-switch' && singleProperty.key === 'geoCoordinates' ? (
        <Geofencing
          singleProperty={singleProperty}
          otherDetails={otherDetails}
          propertiesList={propertiesList}
          dataSourcesList={dataSourcesList}
          properties={fieldSectionProperties}
          geofenceEnabledObject={geofenceEnabledObject}
          handleChange={(name, value) => {
            onChangeProperties(name, value, toggleBox.defaultPropsKey + 'Properties');
          }}
        />
      ) : singleProperty.type === 'toggle' && singleProperty.key === 'ticketSetting' ? (
        <TicketDetailProperties
          properties={fieldSectionProperties}
          handleChange={(name, value) => {
            onChangeProperties(name, value, toggleBox.defaultPropsKey + 'Properties');
          }}
          ticketTypeOptions={ticketTypeOptions}
        />
      ) : singleProperty.type === 'toggle' && singleProperty.key === 'repeatedSection' ? (
        <div>
          <div className='d-flex gap-2 align-items-center'>
            <ToggleSwitch
              name={'sectionRepeated'}
              value={fieldSectionProperties['sectionRepeated']}
              handleChange={(name, value) =>
                onChangeProperties(name, value, toggleBox.defaultPropsKey + 'Properties')
              }
            />
            <label>{t('field_repeated_section')}</label>
          </div>
          {fieldSectionProperties['sectionRepeated'] && (
            <PointerField
              name='sectionMaxRepetition'
              pointer={fieldSectionProperties['sectionMaxRepetition']}
              displayName='form_field_max_repetitions'
              handleChange={(name, value) =>
                onChangeProperties(name, value, toggleBox.defaultPropsKey + 'Properties')
              }
              withInput
              shrink
              minWidth
              classes='mt-2'
            />
          )}
        </div>
      ) : singleProperty.type === 'toggle' && singleProperty.key === 'companyFieldId' ? (
        <>
          <CompanyFieldBasicProperties
            companyFieldList={companyFieldList}
            selectedCompanyFieldIds={selectedCompanyFieldIds}
            companyFieldMap={companyFieldMap}
            companyFieldDisplayValues={companyFieldDisplayValues}
            otherDetails={otherDetails}
            properties={fieldSectionProperties}
            handleChange={(name, value, extra) => {
              onChangeProperties(name, value, toggleBox.defaultPropsKey + 'Properties', extra);
            }}
          />
        </>
      ) : singleProperty.type === 'paragraph' ? (
        <span>{t(singleProperty.text)}</span>
      ) : singleProperty.type === 'checkbox' ? (
        <>
          <SingleCheckBoxField
            disabled={
              (singleProperty.disabledOn?.length > 0
                ? singleProperty.disabledOn.some(
                    (key) => fieldSectionProperties[key] === singleProperty.disabledCondition,
                  )
                : false) || hasTriggers
            }
            name={singleProperty.key}
            value={fieldSectionProperties[singleProperty.key]}
            handleChange={(name, value) => {
              onChangeProperties(name, value, toggleBox.defaultPropsKey + 'Properties');
            }}
            text={<span>{t(singleProperty.text)}</span>}
            shrink
          />
          {singleProperty.dependantElementsList && fieldSectionProperties[singleProperty.key] && (
            <div
              className={`d-flex mt-3 gap-2 ms-4 flex-wrap align-items-center dependentsList ${
                singleProperty.text === 'form_field_date_range' ? 'flex-column' : ''
              } `}
            >
              {singleProperty.dependantElementsList.map((singleDependantField, index) => {
                if (
                  (singleDependantField.isAllowedForAll &&
                    singleDependantField.restrictedFieldTypes.indexOf(selectedField.elementType) ===
                      -1) ||
                  singleDependantField.allowedFieldTypes.indexOf(selectedField.elementType) > -1
                ) {
                  return (
                    <SingleFieldProperties
                      key={'dep' + index}
                      onChangeProperties={onChangeProperties}
                      singleProperty={singleDependantField}
                      fieldSectionProperties={fieldSectionProperties}
                      toggleBox={toggleBox}
                      propertiesList={propertiesList}
                      otherDetails={otherDetails}
                      dataSourcesList={[]}
                      companyFieldList={[]}
                      companyFieldMap={{}}
                      selectedCompanyFieldIds={[]}
                      selectedField={selectedField}
                      ticketTypeList={[]}
                    />
                  );
                }
              })}
            </div>
          )}
        </>
      ) : singleProperty.type === 'dateWithTag' ? (
        <DateFieldWithTag
          name={singleProperty.key}
          tagText={t(singleProperty.text)}
          value={fieldSectionProperties[singleProperty.key]}
          handleChange={(name, value) => {
            onChangeProperties(name, value, toggleBox.defaultPropsKey + 'Properties');
          }}
          shrink
        />
      ) : singleProperty.type === 'numberWithTag' ? (
        <NumberFieldWithTag
          name={singleProperty.key}
          tagText={t(singleProperty.text)}
          value={fieldSectionProperties[singleProperty.key]}
          handleChange={(name, value) => {
            onChangeProperties(name, value, toggleBox.defaultPropsKey + 'Properties');
          }}
          shrink
        />
      ) : singleProperty.type === 'toggle' && singleProperty.key === 'enableWatermark' ? (
        <WatermarkComponent
          singleProperty={singleProperty}
          fieldSectionProperties={fieldSectionProperties}
          toggleBox={toggleBox}
          onChangeProperties={onChangeProperties}
          formElemetsOptionArray={allFormElemetsOptionArray}
        />
      ) : singleProperty.type === 'imagePicker' ? (
        <ImageFilePicker
          properties={fieldSectionProperties}
          handleChange={(name, value) => {
            onChangeProperties(name, value, toggleBox.defaultPropsKey + 'Properties');
          }}
        />
      ) : singleProperty.type === 'text' ? (
        <TextField
          disabled={
            (singleProperty.disabledOn
              ? fieldSectionProperties[singleProperty.disabledOn].toString() ===
                singleProperty.disabledCondition.toString()
              : false) || singleProperty.key === 'fieldUniqueId'
          }
          name={singleProperty.key}
          value={fieldSectionProperties[singleProperty.key]}
          displayName={singleProperty.text}
          handleChange={(name, value) => {
            if (name === 'fieldUniqueId') {
              if (validateSqlColumnName(value))
                onChangeProperties(name, value, toggleBox.defaultPropsKey + 'Properties');
            } else {
              onChangeProperties(name, value, toggleBox.defaultPropsKey + 'Properties');
            }
          }}
          shrink
        />
      ) : singleProperty.type === 'textarea' ? (
        <TextArea
          disabled={
            (singleProperty.disabledOn
              ? fieldSectionProperties[singleProperty.disabledOn].toString() ===
                singleProperty.disabledCondition.toString()
              : false) || singleProperty.key === 'fieldUniqueId'
          }
          name={singleProperty.key}
          value={fieldSectionProperties[singleProperty.key]}
          displayName={singleProperty.text}
          handleChange={(name, value) => {
            if (name === 'fieldUniqueId') {
              if (validateSqlColumnName(value))
                onChangeProperties(name, value, toggleBox.defaultPropsKey + 'Properties');
            } else {
              onChangeProperties(name, value, toggleBox.defaultPropsKey + 'Properties');
            }
          }}
          shrink
          rows={3}
          withoutNewline
          maxLength={maxElementTitleLength}
        />
      ) : singleProperty.type === 'ColorPicker' ? (
        <ColorField
          name={singleProperty.key}
          displayName={t(singleProperty.text)}
          value={fieldSectionProperties[singleProperty.key + 'Color']}
          handleChange={(name, value) => {
            onChangeProperties(name, value, toggleBox.defaultPropsKey + 'Properties');
          }}
          shrink
        />
      ) : singleProperty.type === 'slider' ? (
        <>
          <label className='theme-size-0_8'>{t('form_choose_number_of_range')}</label>
          <div className='d-flex gap-2 align-items-center mb-3'>
            <NumberField
              name={singleProperty.minRangeKey}
              value={fieldSectionProperties[singleProperty.minRangeKey]}
              handleChange={(name, value) => {
                onChangeProperties(name, value, toggleBox.defaultPropsKey + 'Properties');
              }}
              shrink
            />
            <span>{t('form_to')}</span>
            <NumberField
              name={singleProperty.maxRangeKey}
              value={fieldSectionProperties[singleProperty.maxRangeKey]}
              handleChange={(name, value) => {
                onChangeProperties(name, value, toggleBox.defaultPropsKey + 'Properties');
              }}
              shrink
            />
          </div>
        </>
      ) : singleProperty.type === 'rating' ? (
        <>
          <label className='theme-size-0_8'>{t('form_field_number_of_rating')}</label>
          <div className='d-flex gap-2 align-items-center mb-3'>
            <TextField
              name={singleProperty.lowLabelKey}
              value={fieldSectionProperties[singleProperty.lowLabelKey]}
              handleChange={(name, value) => {
                onChangeProperties(name, value, toggleBox.defaultPropsKey + 'Properties');
              }}
              shrink
            />
            <RatingBox
              rating={fieldSectionProperties[singleProperty.key]}
              changeRating={(value) => {
                if (value > 1 && value < 10)
                  onChangeProperties(
                    singleProperty.key,
                    value,
                    toggleBox.defaultPropsKey + 'Properties',
                  );
              }}
            />
            <TextField
              name={singleProperty.highLabelKey}
              value={fieldSectionProperties[singleProperty.highLabelKey]}
              handleChange={(name, value) => {
                onChangeProperties(name, value, toggleBox.defaultPropsKey + 'Properties');
              }}
              shrink
            />
          </div>
        </>
      ) : singleProperty.type === 'dropdownIcon' ? (
        <SelectIconField
          handleChange={(name, value) => {
            onChangeProperties(name, value, toggleBox.defaultPropsKey + 'Properties');
          }}
          name={singleProperty.key}
          placeholder={t(singleProperty.key)}
          label={fieldSectionProperties[singleProperty.key]}
          icon={singleProperty.icon}
          variant='white-1'
          isAutoClose={true}
          iconType='rating'
        />
      ) : singleProperty.type === 'dropdown' ? (
        <SelectField
          placeholder={singleProperty.text}
          label={
            singleProperty.valuesList.filter(
              (singleValue) => singleValue.value === fieldSectionProperties[singleProperty.key],
            )[0].displayValue
          }
          listValues={singleProperty.valuesList}
          icon={singleProperty.icon}
          handleChange={(name, value) => {
            onChangeProperties(name, value, toggleBox.defaultPropsKey + 'Properties');
          }}
          selectedValues={[]}
          name={singleProperty.key}
          isAutoClose={true}
          variant='white-1'
          menuClasses={'w-100'}
          shrink
          filedClasses='w-100'
        />
      ) : singleProperty.type === 'textWithColor' || singleProperty.type === 'textareaWithColor' ? (
        <div className='d-flex gap-2 align-items-end'>
          {singleProperty.type === 'textWithColor' ? (
            <TextField
              name={singleProperty.key}
              value={fieldSectionProperties[singleProperty.key]}
              displayName={singleProperty.text}
              handleChange={(name, value) => {
                onChangeProperties(name, value, toggleBox.defaultPropsKey + 'Properties');
              }}
              shrink
            />
          ) : (
            <TextArea
              name={singleProperty.key}
              value={fieldSectionProperties[singleProperty.key]}
              displayName={singleProperty.text}
              handleChange={(name, value) => {
                onChangeProperties(name, value, toggleBox.defaultPropsKey + 'Properties');
              }}
              shrink
              rows={3}
              withoutNewline
              maxLength={maxElementTitleLength}
              firstCharDirection
            />
          )}
          <div className='position-relative'>
            <FormButton
              text='button_edit'
              variant='green-1'
              icon={editIcon}
              iconRight
              minWidth
              classes='px-2'
              onClick={() => {
                setOpenColorTypeModal(singleProperty.key);
                setOpenColorModal(true);
              }}
            />
            {openColorModal && openColorTypeModal === singleProperty.key && (
              <ColorChangeModal
                name={singleProperty.key}
                closeModal={() => setOpenColorModal(false)}
                styleProperties={propertiesList[toggleBox.defaultPropsKey + 'Properties'] ?? {}}
                handleChange={(name, value) => {
                  onChangeProperties(name, value, toggleBox.defaultPropsKey + 'Properties');
                }}
              />
            )}
          </div>
        </div>
      ) : singleProperty.type === 'toggle' && singleProperty.key === 'otpMessageTemplate' ? (
        <OtpMessage
          singleProperty={singleProperty}
          fieldSectionProperties={fieldSectionProperties}
          toggleBox={toggleBox}
          onChangeProperties={onChangeProperties}
        />
      ) : (
        singleProperty.type
      )}
    </div>
  );
}
SingleFieldProperties.propTypes = {
  onChangeProperties: PropTypes.func.isRequired,
  singleProperty: PropTypes.object.isRequired,
  otherDetails: PropTypes.object.isRequired,
  fieldSectionProperties: PropTypes.object.isRequired,
  toggleBox: PropTypes.object.isRequired,
  propertiesList: PropTypes.object.isRequired,
  selectedField: PropTypes.object.isRequired,
  dataSourcesList: PropTypes.array.isRequired,
  companyFieldList: PropTypes.array.isRequired,
  companyFieldMap: PropTypes.object,
  companyFieldDisplayValues: PropTypes.object,
  ticketTypeList: PropTypes.array.isRequired,
  selectedCompanyFieldIds: PropTypes.array.isRequired,
  hasTriggers: PropTypes.bool,
  geofenceEnabledObject: PropTypes.object.isRequired,
};
export default SingleFieldProperties;
